import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Upload, Sparkles, Loader2, CheckCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import * as XLSX from "xlsx";

interface CleaningResult {
  original_rows: number;
  cleaned_rows: number;
  issues_fixed: string[];
  data: any[];
  natural_language_summary?: string;
}

const ACCEPTED = ".csv,.xlsx,.xls,.json";

async function parseAnyFile(file: File): Promise<any[]> {
  const name = file.name.toLowerCase();

  if (name.endsWith(".json")) {
    const text = await file.text();
    const parsed = JSON.parse(text);
    return Array.isArray(parsed) ? parsed : [parsed];
  }

  if (name.endsWith(".csv")) {
    const text = await file.text();
    const wb = XLSX.read(text, { type: "string" });
    const sheet = wb.Sheets[wb.SheetNames[0]];
    return XLSX.utils.sheet_to_json(sheet, { defval: "" });
  }

  if (name.endsWith(".xlsx") || name.endsWith(".xls")) {
    const buf = await file.arrayBuffer();
    const wb = XLSX.read(buf, { type: "array" });
    const sheet = wb.Sheets[wb.SheetNames[0]];
    return XLSX.utils.sheet_to_json(sheet, { defval: "" });
  }

  throw new Error(`Unsupported file type: ${file.type || name}. Use CSV, XLSX, XLS, or JSON.`);
}

function cleanRows(rows: any[]): { cleaned: any[]; fixed: string[] } {
  if (rows.length === 0) return { cleaned: [], fixed: [] };
  const fixed: string[] = [];

  // Trim strings, drop fully-empty rows
  let cleaned = rows.map((r) => {
    const obj: any = {};
    for (const [k, v] of Object.entries(r)) {
      obj[k.trim()] = typeof v === "string" ? v.trim() : v;
    }
    return obj;
  }).filter((r) => Object.values(r).some((v) => v !== "" && v != null));

  if (cleaned.length < rows.length) {
    fixed.push(`Removed ${rows.length - cleaned.length} fully-empty row(s)`);
  }

  // Dedupe identical rows
  const seen = new Set<string>();
  const beforeDedupe = cleaned.length;
  cleaned = cleaned.filter((r) => {
    const key = JSON.stringify(r);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  if (beforeDedupe - cleaned.length > 0) {
    fixed.push(`Removed ${beforeDedupe - cleaned.length} duplicate row(s)`);
  }

  // Coerce numeric-looking strings
  const sample = cleaned.slice(0, 50);
  const cols = Object.keys(cleaned[0]);
  for (const col of cols) {
    const vals = sample.map((r) => r[col]).filter((v) => v !== "" && v != null);
    if (vals.length > 0 && vals.every((v) => !isNaN(Number(v)))) {
      let coerced = 0;
      cleaned.forEach((r) => {
        if (typeof r[col] === "string" && r[col] !== "" && !isNaN(Number(r[col]))) {
          r[col] = Number(r[col]);
          coerced++;
        }
      });
      if (coerced > 0) fixed.push(`Coerced "${col}" to numeric (${coerced} values)`);
    }
  }
  return { cleaned, fixed };
}

export const AIDataCleaner = () => {
  const [file, setFile] = useState<File | null>(null);
  const [industry, setIndustry] = useState("universal");
  const [progress, setProgress] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [cleaningResult, setCleaningResult] = useState<CleaningResult | null>(null);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const f = event.target.files?.[0];
    if (f) { setFile(f); setCleaningResult(null); }
  };

  const cleanDataWithAI = async () => {
    if (!file) {
      toast.error("Please select a file");
      return;
    }
    setIsProcessing(true);
    setProgress(10);

    try {
      setProgress(30);
      let rows: any[] = [];
      try {
        rows = await parseAnyFile(file);
      } catch (e: any) {
        toast.error(e.message || "Failed to parse file");
        return;
      }

      if (!rows || rows.length === 0) {
        toast.error(`Could not parse rows from "${file.name}" — check the file format`);
        return;
      }

      setProgress(50);
      const { cleaned, fixed } = cleanRows(rows);
      setProgress(70);

      // Real AI analysis (no mock data)
      const { data: { session } } = await supabase.auth.getSession();
      let aiFixed: string[] = [];
      let summary: string | undefined;
      try {
        const { data, error } = await supabase.functions.invoke("ai-data-processor", {
          body: {
            rows: cleaned.slice(0, 100),
            source_type: industry,
            source_name: file.name,
            analysis_type: "cleaning",
          },
          headers: session ? { Authorization: `Bearer ${session.access_token}` } : undefined,
        });
        if (!error && data?.success && data.analysis) {
          aiFixed = (data.analysis.cleaning_recommendations || [])
            .map((r: any) => `${r.column ? `[${r.column}] ` : ""}${r.issue} — ${r.action}`);
          summary = data.analysis.natural_language_summary;
        }
      } catch (e) {
        console.warn("AI analysis failed, returning deterministic cleaning only", e);
      }

      setProgress(100);
      setCleaningResult({
        original_rows: rows.length,
        cleaned_rows: cleaned.length,
        issues_fixed: [...fixed, ...aiFixed],
        data: cleaned,
        natural_language_summary: summary,
      });
      toast.success("Data cleaning completed!");
    } catch (e: any) {
      console.error("Error cleaning data:", e);
      toast.error(e.message || "Failed to clean data");
    } finally {
      setIsProcessing(false);
    }
  };

  const downloadCleanedData = () => {
    if (!cleaningResult) return;
    const headers = Object.keys(cleaningResult.data[0] || {});
    const csv = [
      headers.join(","),
      ...cleaningResult.data.map((r) =>
        headers.map((h) => {
          const v = r[h];
          const s = v == null ? "" : String(v);
          return s.includes(",") || s.includes('"') ? `"${s.replace(/"/g, '""')}"` : s;
        }).join(",")
      ),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `cleaned_${file?.name.replace(/\.[^/.]+$/, "")}_${Date.now()}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success("File downloaded");
  };

  const saveCleanedData = async () => {
    if (!cleaningResult) return;
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { toast.error("Please sign in"); return; }

      const headers = Object.keys(cleaningResult.data[0] || {});
      const csv = [headers.join(","), ...cleaningResult.data.map((r) => headers.map((h) => r[h] ?? "").join(","))].join("\n");
      const blob = new Blob([csv], { type: "text/csv" });
      const fileName = `cleaned_${file?.name.replace(/\.[^/.]+$/, "")}_${Date.now()}.csv`;
      const filePath = `${user.id}/cleaned/${fileName}`;
      const { error: upErr } = await supabase.storage.from("user_files").upload(filePath, blob);
      if (upErr) throw upErr;
      await supabase.from("file_storage").insert({
        user_id: user.id, file_name: fileName, file_path: filePath,
        folder_path: `/${user.id}/cleaned/`, size: blob.size, mime_type: "text/csv", is_folder: false,
      });
      toast.success("Saved to your file storage");
      window.dispatchEvent(new CustomEvent("data-source-updated"));
    } catch (e: any) {
      toast.error(e.message || "Failed to save");
    }
  };

  return (
    <Card className="p-6">
      <div className="flex items-center gap-2 mb-4">
        <Sparkles className="h-5 w-5 text-primary" />
        <h3 className="text-lg font-semibold">AI Data Cleaner</h3>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-2">Industry</label>
          <Select value={industry} onValueChange={setIndustry}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="universal">Universal / General</SelectItem>
              <SelectItem value="finance">Finance</SelectItem>
              <SelectItem value="ecommerce">E-commerce</SelectItem>
              <SelectItem value="logistics">Logistics</SelectItem>
              <SelectItem value="realestate">Real Estate</SelectItem>
              <SelectItem value="technology">Technology</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">Upload Data File (CSV, XLSX, XLS, JSON)</label>
          <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-6">
            <input type="file" accept={ACCEPTED} onChange={handleFileUpload}
              className="hidden" id="data-file-upload" disabled={isProcessing} />
            <label htmlFor="data-file-upload" className="flex flex-col items-center cursor-pointer">
              <Upload className="h-8 w-8 text-muted-foreground mb-2" />
              <span className="text-sm text-muted-foreground">
                {file ? file.name : "Click to upload your dataset"}
              </span>
            </label>
          </div>
        </div>

        {isProcessing && (
          <div className="space-y-2">
            <div className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /><span className="text-sm">Cleaning data...</span></div>
            <Progress value={progress} className="h-2" />
          </div>
        )}

        <Button onClick={cleanDataWithAI} disabled={!file || isProcessing} className="w-full">
          {isProcessing ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Processing...</>
            : <><Sparkles className="mr-2 h-4 w-4" />Clean Data with AI</>}
        </Button>

        {cleaningResult && (
          <div className="mt-6 p-4 bg-muted rounded-lg">
            <h4 className="font-medium mb-3 flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-500" />Cleaning Results
            </h4>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div><span className="text-sm text-muted-foreground">Original Rows:</span><div className="font-semibold">{cleaningResult.original_rows}</div></div>
              <div><span className="text-sm text-muted-foreground">Cleaned Rows:</span><div className="font-semibold text-green-600">{cleaningResult.cleaned_rows}</div></div>
            </div>
            {cleaningResult.issues_fixed.length > 0 && (
              <div className="mb-4">
                <span className="text-sm text-muted-foreground">Issues Fixed:</span>
                <ul className="mt-1 space-y-1 max-h-40 overflow-y-auto">
                  {cleaningResult.issues_fixed.map((issue, i) => (
                    <li key={i} className="text-sm flex items-start gap-2">
                      <CheckCircle className="h-3 w-3 text-green-500 mt-0.5 flex-shrink-0" />{issue}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {cleaningResult.natural_language_summary && (
              <div className="mb-4 p-3 bg-background rounded text-sm">
                {cleaningResult.natural_language_summary}
              </div>
            )}
            <div className="flex gap-2">
              <Button onClick={saveCleanedData} className="flex-1">Save to Folder</Button>
              <Button onClick={downloadCleanedData} variant="outline" className="flex-1">Download CSV</Button>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};
